import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { Settings, Palette, Globe, Save } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { userService } from '@/services/user.service'
import type { UserPreferencesUpdate } from '@/types/user.types'

export default function SettingsPage() {
  const { toast } = useToast()
  const { user, setUser } = useAuthStore()
  const [isSaving, setIsSaving] = useState(false)

  // Initialize from user preferences
  const prefs = user?.preferences
  const [unitsLength, setUnitsLength] = useState<string>(prefs?.unitsLength || 'metric')
  const [unitsWeight, setUnitsWeight] = useState<string>(prefs?.unitsWeight || 'metric')
  const [unitsTemperature, setUnitsTemperature] = useState<string>(prefs?.unitsTemperature || 'celsius')
  const [unitsSpeed, setUnitsSpeed] = useState<string>(prefs?.unitsSpeed || 'kph')
  const [dashboardColumns, setDashboardColumns] = useState<string>(prefs?.dashboardColumns || '2')
  const [showWeatherWidget, setShowWeatherWidget] = useState(prefs?.showWeatherWidget || false)
  const [timezone, setTimezone] = useState(prefs?.timezone || 'UTC')
  const [locale, setLocale] = useState(prefs?.locale || 'en_US')

  // Sync state when user data changes
  useEffect(() => {
    if (prefs) {
      setUnitsLength(prefs.unitsLength || 'metric')
      setUnitsWeight(prefs.unitsWeight || 'metric')
      setUnitsTemperature(prefs.unitsTemperature || 'celsius')
      setUnitsSpeed(prefs.unitsSpeed || 'kph')
      setDashboardColumns(prefs.dashboardColumns || '2')
      setShowWeatherWidget(prefs.showWeatherWidget || false)
      setTimezone(prefs.timezone || 'UTC')
      setLocale(prefs.locale || 'en_US')
    }
  }, [prefs])

  const saveSettings = async () => {
    if (!user) return
    setIsSaving(true)

    try {
      const update: UserPreferencesUpdate = {
        units_length: unitsLength as 'metric' | 'imperial',
        units_weight: unitsWeight as 'metric' | 'imperial',
        units_temperature: unitsTemperature as 'celsius' | 'fahrenheit',
        units_speed: unitsSpeed as 'kph' | 'mph',
        dashboard_columns: dashboardColumns as '1' | '2' | '3' | '4',
        show_weather_widget: showWeatherWidget,
        timezone,
        locale,
      }

      await userService.updateUserSettings(user.id, update)

      // Update local user state
      if (user) {
        setUser({
          ...user,
          preferences: {
            ...user.preferences,
            unitsLength: unitsLength as 'metric' | 'imperial',
            unitsWeight: unitsWeight as 'metric' | 'imperial',
            unitsTemperature: unitsTemperature as 'celsius' | 'fahrenheit',
            unitsSpeed: unitsSpeed as 'kph' | 'mph',
            dashboardColumns: dashboardColumns as '1' | '2' | '3' | '4',
            showWeatherWidget,
            timezone,
            locale,
          }
        })
      }

      toast({
        title: 'Settings saved',
        description: 'Your preferences have been updated',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your display preferences and units
          </p>
        </div>

        <Tabs defaultValue="units" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="units">Units & Display</TabsTrigger>
            <TabsTrigger value="regional">Regional</TabsTrigger>
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
          </TabsList>

          <TabsContent value="units" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Measurement Units
                </CardTitle>
                <CardDescription>
                  Configure how measurements are displayed across the application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Length / Distance</Label>
                    <Select value={unitsLength} onValueChange={setUnitsLength}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="metric">Metric (mm, m)</SelectItem>
                        <SelectItem value="imperial">Imperial (in, ft)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Weight</Label>
                    <Select value={unitsWeight} onValueChange={setUnitsWeight}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="metric">Metric (kg, L)</SelectItem>
                        <SelectItem value="imperial">Imperial (lb, gal)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Temperature</Label>
                    <Select value={unitsTemperature} onValueChange={setUnitsTemperature}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="celsius">Celsius</SelectItem>
                        <SelectItem value="fahrenheit">Fahrenheit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Wind Speed</Label>
                    <Select value={unitsSpeed} onValueChange={setUnitsSpeed}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kph">km/h</SelectItem>
                        <SelectItem value="mph">mph</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dashboard</CardTitle>
                <CardDescription>Configure dashboard layout</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Dashboard Columns</Label>
                  <Select value={dashboardColumns} onValueChange={setDashboardColumns}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Column</SelectItem>
                      <SelectItem value="2">2 Columns</SelectItem>
                      <SelectItem value="3">3 Columns</SelectItem>
                      <SelectItem value="4">4 Columns</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Show Weather Widget</Label>
                    <p className="text-sm text-muted-foreground">
                      Display weather information on the dashboard
                    </p>
                  </div>
                  <Switch
                    checked={showWeatherWidget}
                    onCheckedChange={setShowWeatherWidget}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="regional" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Regional Settings
                </CardTitle>
                <CardDescription>
                  Configure timezone and language preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pacific/Auckland">New Zealand (NZST)</SelectItem>
                        <SelectItem value="Australia/Sydney">Sydney (AEST)</SelectItem>
                        <SelectItem value="Australia/Perth">Perth (AWST)</SelectItem>
                        <SelectItem value="Asia/Singapore">Singapore (SGT)</SelectItem>
                        <SelectItem value="Europe/London">London (GMT)</SelectItem>
                        <SelectItem value="America/New_York">New York (EST)</SelectItem>
                        <SelectItem value="America/Los_Angeles">Los Angeles (PST)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select value={locale} onValueChange={setLocale}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en_NZ">English (NZ)</SelectItem>
                        <SelectItem value="en_AU">English (AU)</SelectItem>
                        <SelectItem value="en_US">English (US)</SelectItem>
                        <SelectItem value="en_GB">English (UK)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="appearance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Appearance
                </CardTitle>
                <CardDescription>
                  Customize the look and feel of the application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Theme</Label>
                  <Select defaultValue="system">
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Theme preference is stored locally in your browser
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  )
}
